import {
  getObjectAtSystemDefinitionSlot,
  getProductsBySlotNameAndId,
} from '../../project-definition-data-access';
import ControlProductInfo from './ControlProductInfo';
import {
  findProductAccessory,
  instantiate as defaultInstantiate,
  getAccessoriesArrayByProduct as defGetAccessoriesArrayByProduct,
  addAccessoryToArray as defAddAccessoryToArray,
  addOptionalAccessoryToArray as defAddOptionalAccessoryToArray,
  removeAccessory as defRemoveAccessory,
  addAccessory as defAddAccessory,
  addOptionalAccessory as defAddOptionalAccessory,
} from '../defaults';

/*
function filterKeepInAllSlots(item) {
  return item && item.keep_in_all_slots;
}
*/

export function instantiate(product, type, systemDefinition, systemProducts, basedOnObject) {
  if (!product) {
    return null;
  }
  const result = defaultInstantiate(product, type, systemDefinition, systemProducts, basedOnObject);
  if (!result) {
    return null;
  }
  result.property = product.set_identifier;

  return result;
}

// Returns the set the control belongs to
// In most cases stored in slot.property,
// in case of null, it will search for its product in the system products
function getControlSetBySlotName(slotName, systemDefinition, systemProducts, assignIfNeeded = true) {
  const { slots } = systemDefinition.attributes;
  const data = slots[slotName];
  if (!data) {
    return null;
  }
  const { id, property } = data;
  if (property) {
    return property;
  }
  const idString = `${id}`; // Convert to string to get comparison working

  const { sets } = systemProducts.attributes;
  const setsKeys = Object.keys(sets);
  const numSetsKeys = setsKeys.length;
  let resProduct = null;
  let resPoints = -1;
  let resAmount = -1;
  let resSet = null;
  for (let i = 0; i < numSetsKeys; i += 1) {
    const nameOfSet = `control-${i + 1}`;
    const set = sets[nameOfSet];
    if (set) {
      const numProducts = set.length;
      for (let j = 0; j < numProducts; j += 1) {
        const product = set[j];
        const productId = `${product.id}`; // Convert to string to get comparison working
        if (productId === idString) {
          let points = 0;
          if (product.amount > resAmount) {
            resAmount = product.amount;
            points += 1;
          }
          if (product.required) {
            points += 2;
          }
          if (!resProduct || resPoints < 1 || (points > resPoints)) {
            resProduct = product;
            resPoints = points;
            resSet = nameOfSet;
          }
        }
      }
    }
  }
  if (assignIfNeeded) {
    data.property = resSet;
  }
  return resSet;
}

/*
  Group control names by id & set (property)
  Result should be something like this:
  {
    groups: [
      {
        id: 0,
        set: 'control-1'
        slotNames: ['controls-1', ...]
      }
    ],
    groupsBySlotName: {
      'controls-1': {
        id: 0,
        set: 'control-1',
        slotNames: ['controls-1', ...]
      }
    }
  }
*/

export function getProductInfoBySlotName(slotName, systemDefinition, systemProducts) {
  const data = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
  if (!data) {
    return null;
  }
  const { property } = data;
  const { sets } = systemProducts.attributes;
  const numSets = Object.keys(sets).length;
  for (let i = 0; i < numSets; i += 1) {
    const name = `control-${i + 1}`;
    const set = sets[name];
    if (set) {
      for (let j = 0, length = set.length; j < length; j += 1) {
        const product = set[j];
        if (product.set_identifier === property) {
          return product;
        }
      }
    }
  }
  // Not found, check old_sets
  for (let i = 0; i < numSets; i += 1) {
    const name = `control-${i + 1}`;
    const set = sets[name];
    if (set) {
      for (let j = 0, length = set.length; j < length; j += 1) {
        const product = set[j];
        const oldSets = product ? product.old_sets : null;
        if (oldSets && oldSets.indexOf(property, 0) >= 0) {
          // #if DEBUG
          console.warn('Found control using old_sets (', property, product, ')');
          // #endif
          return product;
        }
      }
    }
  }
  return null;
}

function productHasAccessoryId(product, id, onlyOptionalAccessories) {
  if (!product) {
    return false;
  }

  const accessories = defGetAccessoriesArrayByProduct(product, onlyOptionalAccessories);
  if (!accessories) {
    return false;
  }
  const num = accessories.length;
  for (let i = 0; i < num; i += 1) {
    const acc = accessories[i];
    if (acc && acc.id === id) {
      return true;
    }
  }
  return false;
}

/*
  Move all accessories with
    'accessory.keep_in_all_slots === false'
  to the first controller
    or to the first wireless controller if accessory.only_wireless === true
*/

function reorderAccessoriesIfNeeded(systemDefinition, systemProducts) {
  if (!systemDefinition || !systemProducts) {
    return;
  }
  const { attributes: { slots } } = systemDefinition;
  const numKeys = Object.keys(slots).length;
  for (let i = 0; i < numKeys; i += 1) {
    const slotName = `controls-${i + 1}`;
    const slotValue = slots[slotName];
    if (slotValue) {
      let { options } = slotValue;
      let numOptions = options ? options.length : 0;
      let tempOptions = options;

      // Check if all controls have valid options
      const product = getProductInfoBySlotName(slotName, systemDefinition, systemProducts);
      for (let j = 0; j < numOptions; j += 1) {
        const opt = tempOptions[j];
        const optId = opt ? opt.id : null;
        if (
          !productHasAccessoryId(product, optId, false) &&
          !productHasAccessoryId(product, optId, true) // optional accessories
        ) {
          if (tempOptions === options) {
            // Take a copy in this rare case,
            // because there is going to be an item removed from the original options array
            tempOptions = options.concat();
          }
          defRemoveAccessory(slotName, opt, systemDefinition, systemProducts);
        }
      }
      options = tempOptions;
      numOptions = options ? options.length : 0;

      if (i > 0) {
        for (let j = 0; j < numOptions; j += 1) {
          const option = tempOptions[j];
          if (option) {
            const optId = option.id;
            let loopProducts = true;
            for (let k = 0; loopProducts && k < i; k += 1) {
              const otherSlotName = `controls-${k + 1}`;
              if (otherSlotName === slotName) {
                loopProducts = false;
              } else {
                const otherProduct = getProductInfoBySlotName(otherSlotName, systemDefinition, systemProducts);
                if (otherProduct) {
                  // bookmark
                  const hasAccessoryId = productHasAccessoryId(otherProduct, optId, false);
                  const hasOptionalAccessoryId = productHasAccessoryId(otherProduct, optId, true);
                  if (hasAccessoryId || hasOptionalAccessoryId) {
                    loopProducts = false;
                    if (tempOptions === options) {
                      tempOptions = options.concat();
                    }
                    defRemoveAccessory(slotName, option, systemDefinition, systemProducts);
                    if (hasAccessoryId) {
                      defAddAccessory(otherSlotName, option, systemDefinition, systemProducts);
                    } else if (hasOptionalAccessoryId) {
                      const optionSet = option ? option.set : null;
                      defAddOptionalAccessory(otherSlotName, option, optionSet, systemDefinition, systemProducts);
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}

// Keep this for now
// TODO: remove later
export function oldReorderAccessoriesIfNeeded(systemDefinition, systemProducts) {
  if (!systemDefinition || !systemProducts) {
    return;
  }
  const { attributes: { slots } } = systemDefinition;
  const numKeys = Object.keys(slots).length;

  /*
  Pseudo-code
  for each controllerslot in systemDefinition
    controller = slotValue(controllerSlot, systemDefinition)
    product = productOf(controller)
    for each accessory in controller.options
      currentcontrollerslot = controllerslot
      for otherControllerSlot:  firstcontroller to currentcontroller - 1
        if (accessoryInControllerSlot(otherControllerSlot, accessory.id))
          otherController = slotValue(otherControllerSlot, systemDefinition)

          removeAccessory(controller, accessory.id);
          addSingleAccessory(otherController, instantiateAccessory(accessory.id))
  */

  for (let i = 0; i < numKeys; i += 1) {
    const slotName = `controls-${i + 1}`;
    const slotValue = slots[slotName];
    if (slotValue) {
      let firstController = null;
      let firstControllerProduct = null;

      const controlProduct = getProductInfoBySlotName(slotName, systemDefinition, systemProducts);
      const isWireless = controlProduct ? controlProduct.wireless : false;

      for (let j = 0; !firstController && j < numKeys; j += 1) {
        const otherSlotName = `controls-${j + 1}`;
        const otherSlotValue = slots[otherSlotName];
        if (otherSlotValue) {
          if (otherSlotValue.id === slotValue.id) {
            if (!firstController) {
              firstController = otherSlotValue;
              firstControllerProduct = getProductInfoBySlotName(otherSlotName, systemDefinition, systemProducts);
            }
          }
        }
      }

      if (slotValue !== firstController) {
        // Move all accessories with keep_in_all_slots==false to first controller
        const { options } = slotValue;
        const numOptions = options ? options.length : 0;
        for (let j = 0; j < numOptions; j += 1) {
          const opt = options[i];
          const optId = opt ? opt.id : null;
          if (!opt.keep_in_all_slots && productHasAccessoryId(firstControllerProduct, optId)) {
            console.log('');
          }
        }
      }
      if (isWireless) {
        // Move all accessories with keep_in_all_slots==false && only_wireless
        // to first wireless controller that has this accessory
        console.log('');
      }

      console.log('Controller', slotName, slotValue, controlProduct);
    }
  }
}

// TODO: should merge this function with utils/project-definition/config-layout -> getProductInfoByElement
function findControlProduct(id, set, systemProducts, amount = -1, controlPlacement = null, required = -1) {
  const { sets } = systemProducts.attributes;
  if (!set) {
    // Search in all sets
    const setsKeys = Object.keys(sets);
    const numKeys = setsKeys ? setsKeys.length : 0;
    for (let i = 0; i < numKeys; i += 1) {
      const name = `control-${i + 1}`;
      const product = findControlProduct(id, name, systemProducts, amount, controlPlacement);
      if (product) {
        return product;
      }
    }
    return null;
  }
  const controls = sets[set];
  if (!controls) {
    return null;
  }
  const numControls = controls.length;
  const idString = `${id}`;
  let result = null;
  for (let i = 0; i < numControls; i += 1) {
    const control = controls[i];
    const controlId = `${control.id}`;
    if (controlId === idString) {
      if (
        (typeof (amount) !== 'number' || amount < 0 || control.amount === amount)
        &&
        (!controlPlacement || control.control_placement === controlPlacement)
      ) {
        if ((required !== -1) && ((!!required) === (!!control.required))) {
          return control;
        } else if (!result || control.required) {
          result = control;
        }
      }
    }
  }
  return result;
}

// Returns an object containing all slotnames where controls from the same set are stored
export function getGroupedSlots(slotName, systemDefinition, systemProducts) {
  const { slots } = systemDefinition.attributes;

  // Get product data from system definition
  const data = slots[slotName];
  // Get id from product data
  // Get property from product data (= name of the set in the system products)
  const { id } = data;
  const property = getControlSetBySlotName(slotName, systemDefinition, systemProducts);
  const result = {};

  let controlProduct = findControlProduct(id, property, systemProducts);

  result.product = controlProduct;

  if (!controlProduct || !property) {
    result.slotNames = [slotName];
    return result;
  }
  const slotNames = [];
  result.slotNames = slotNames;
  const numSlots = Object.keys(slots).length;
  for (let i = 0; i < numSlots; i += 1) {
    const name = `controls-${i + 1}`;
    const otherData = slots[name];
    if (otherData) {
      if (otherData.id === id && otherData.property === property) {
        slotNames.push(name);
      }
    }
  }

  const count = slotNames.length;
  if (controlProduct.amount !== count) {
    controlProduct = findControlProduct(id, property, systemProducts, count);
    result.product = controlProduct;
  }

  return result;
}

// Max allowed slots per row
const MAX_SLOTS_CONTROL_PLACEMENT = {
  controls: 3, // first row
  circuit: 3, // first row (alias of 'controls')
  kettle: 2, // second row
};

function getControlProductInfoBySlotName(slotName, systemDefinition, systemProducts, result = null) {
  const productData = systemDefinition.attributes.slots[slotName];
  const res = result;
  if (res) {
    res.set = null;
    res.product = null;
  }
  if (!productData) {
    return res;
  }
  const { property } = productData;
  const { sets } = systemProducts.attributes;
  const numSets = Object.keys(sets).length;
  for (let i = 0; i < numSets; i += 1) {
    const setName = `control-${i + 1}`;
    const set = sets[setName];
    if (set) {
      const numProducts = set.length;
      for (let j = 0; j < numProducts; j += 1) {
        const productInfo = set[j];
        if (productInfo.set_identifier === property) {
          if (result) {
            res.set = setName;
            res.product = productInfo;
            return res;
          }
          return new ControlProductInfo(productInfo, setName);
        }
      }
    }
  }

  for (let i = 0; i < numSets; i += 1) {
    const name = `control-${i + 1}`;
    const set = sets[name];
    if (set) {
      for (let j = 0, length = set.length; j < length; j += 1) {
        const product = set[j];
        const oldSets = product ? product.old_sets : null;
        if (oldSets && oldSets.indexOf(property, 0) >= 0) {
          // #if DEBUG
          console.warn('Found control using old_sets (', property, product, ')');
          // #endif
          if (result) {
            res.set = name;
            res.product = product;
            return res;
          }
          return new ControlProductInfo(product, name);
        }
      }
    }
  }

  return res;
}

/**
 * @function getAvailableNewProductsForSystemDefinition
 * @description
 * */
export function getAvailableNewProductsForSystemDefinition(systemDefinition, systemProducts) {
  const result = [];
  const { slots } = systemDefinition.attributes;
  const slotKeys = Object.keys(slots);
  const numSlotKeys = slotKeys.length;

  // Count the used slots per row (circuit/kettle) in the system definition
  const usedSlots = {};
  const usedSets = {};
  const setIdentifierCounts = {};

  for (let i = 0; i < numSlotKeys; i += 1) {
    const name = `controls-${i + 1}`;
    const slotData = slots[name];
    if (slotData) {
      const controlProductInfo = getControlProductInfoBySlotName(name, systemDefinition, systemProducts);
      const product = controlProductInfo ? controlProductInfo.product : null;
      const set = controlProductInfo ? controlProductInfo.set : null;
      if (set) {
        usedSets[set] = true;
      }
      if (product) {
        const setIdentifier = product.set_identifier;
        if (typeof (setIdentifier) !== 'undefined' && setIdentifier !== null) {
          setIdentifierCounts[setIdentifier] = setIdentifierCounts[setIdentifier] || 0;
          setIdentifierCounts[setIdentifier] += 1;
        }

        const controlPlacement = product.control_placement || 'controls';
        const count = usedSlots[controlPlacement] || 0;
        usedSlots[controlPlacement] = count + 1;
      }
    }
  }

  const { sets } = systemProducts.attributes;
  const numSets = Object.keys(sets).length;

  // Collect control products that can be added
  for (let i = 0; i < numSets; i += 1) {
    const name = `control-${i + 1}`;
    const setIsNotUsed = !usedSets[name];
    const set = sets[name];
    if (set) {
      for (let j = 0, length = set.length; j < length; j += 1) {
        const controlProduct = set[j];
        const { set_identifier, amount } = controlProduct;
        const usedCount = setIdentifierCounts[set_identifier];
        const setIdentifierUsed = usedCount !== null && typeof (usedCount) !== 'undefined';
        const numAvailable = amount - (usedCount || 0);
        // - Only controls can be added from a set that hasn't been used
        // - Only required controls can be added because otherwise you won't be able to remove them
        //   Except when a control is part of a set where amount > 0 and the max amount hasn't been reached yet
        if ((!controlProduct.required && setIsNotUsed) || (setIdentifierUsed && numAvailable > 0)) {
          const controlPlacement = controlProduct.control_placement;
          const controlAmount = 1; // Used to be controlProduct.amount, now just adding one control each time
          const used = usedSlots[controlPlacement] || 0;
          const available = MAX_SLOTS_CONTROL_PLACEMENT[controlPlacement] - used;
          // Product can only be added if the amount is available on that row
          if (available >= controlAmount) {
            // Wrap the product inside a ControlProductInfo instance because the name of the set is also needed
            result.push(new ControlProductInfo(controlProduct, name));
          }
        }
      }
    }
  }

  return result;
}

/**
 * @function getAvailableReplacementProductsForSystemDefinition
 * @description
 * */
export function getAvailableReplacementProductsForSystemDefinition(slotName, systemDefinition, systemProducts) { // eslint-disable-line
  const productData = systemDefinition.attributes.slots[slotName];
  if (!productData) {
    return null;
  }

  const controlProductInfo = getControlProductInfoBySlotName(slotName, systemDefinition, systemProducts);
  if (!controlProductInfo || !controlProductInfo.set || !controlProductInfo.product) {
    return null;
  }

  // const controlsPerRow = groupControlSlotsPerRow(systemDefinition, systemProducts);
  const currentRowName = controlProductInfo.product.control_placement;
  // const row = controlsPerRow[currentRowName];

  const { property } = productData;

  /*
  const availableInRow = MAX_SLOTS_CONTROL_PLACEMENT[currentRowName] - row.length;
  const productAmount = controlProductInfo.product.amount;
  let amountLeft = productAmount;
  for (let i = row.length - 1; i >= 0; i -= 1) {
    const item = row[i];
    if (item.productInfo.set_identifier === property) {
      // Don't take current set of controls into account
      if (amountLeft > 0) {
        amountLeft -= 1;
        availableInRow += 1;
      }
    }
  }
  */

  const required = !!controlProductInfo.product.required;

  const { sets } = systemProducts.attributes;
  const controlSetName = controlProductInfo.set;
  const resultArray = [];
  const set = sets[controlSetName];
  if (set) {
    const numProducts = set.length;
    for (let j = 0; j < numProducts; j += 1) {
      const productInfo = set[j];
      if (productInfo.set_identifier !== property &&
        productInfo.control_placement === currentRowName &&
        (!!productInfo.required) === required
      ) {
        resultArray.push(new ControlProductInfo(productInfo, controlSetName));
      }
    }
  }
  return resultArray;
}

/**
 * @function canAddProductToSystemDefinition
 * @return {boolean} - True if new controls can be added to the system definition
 * */
export function canAddAnyProductToSystemDefinition(type, systemDefinition, systemProducts) {
  // Reusing function 'getAvailableNewProductsForSystemDefinition'...I'm sooo lazy
  const allowedNewProducts = getAvailableNewProductsForSystemDefinition(systemDefinition, systemProducts);
  return allowedNewProducts ? allowedNewProducts.length > 0 : false;
}

/**
 * @function canAddProductToSystemDefinition
 * @return {boolean} - True if the control can be added to the system definition
 * */
export function canAddProductToSystemDefinition(type, systemDefinition, systemProducts, product) { // eslint-disable-line
  return canAddAnyProductToSystemDefinition(type, systemDefinition, systemProducts);
}

/**
 * @function canReplaceControlInSystemDefinition
 * @description
 * */
export function canReplaceProductInSystemDefinition(slotName, systemDefinition, systemProducts, product) { // eslint-disable-line
  // Reusing function 'getAvailableReplacementProductsForSystemDefinition'...I'm sooo lazy
  const replacingProducts = getAvailableReplacementProductsForSystemDefinition(slotName, systemDefinition, systemProducts);
  return replacingProducts ? replacingProducts.length > 0 : false;
}

/**
 * @function canRemoveControlFromSystemDefinition
 * @description
 * */
export function canRemoveProductFromSystemDefinition(slot, systemDefinition, systemProducts, product) { // eslint-disable-line
  const data = systemDefinition.attributes.slots[slot];
  const { id } = data;
  const property = getControlSetBySlotName(slot, systemDefinition, systemProducts);
  const productInfo = product || findControlProduct(id, property, systemProducts);
  if (!productInfo) {
    // Shouldn't happen!
    // #if DEBUG
    console.warn('Slot ', slot, 'has no product info!', systemDefinition, systemProducts);
    // #endif
    return true;
  }
  return !productInfo.required;
}

function controlSortCompare(object1, object2) {
  const control1 = object1.slotData;
  const control2 = object2.slotData;
  if (control1 === control2) {
    return 0;
  }
  if (!control1) {
    return 1;
  }
  if (!control2) {
    return -1;
  }
  const property1 = control1.property;
  const property2 = control2.property;

  if (property1 === property2) {
    return 0;
  }

  const slotName1 = object1.slotName;
  const slotName2 = object2.slotName;

  if (slotName1 !== slotName2) {
    if (!slotName1) {
      return 1;
    } else if (!slotName2) {
      return -1;
    }
    if (slotName1 > slotName2) {
      return 1;
    } else if (slotName1 < slotName2) {
      return -1;
    }
  }


  if (property1 > property2) {
    return 1;
  }
  return -1;
}

/**
 * @function replaceProductInSystemDefinition
 * @description Replaces a product at a given slot in the system definition
 * @param {string} slotName - name of the slot in the system definition (controls-1, controls-2, ...)
 * @param {object} productData - object containing {product, set}.
 * 'product' is the object in the systemProducts, 'set' is the name of the set inside the systemProducts (string)
 * @param {object} systemDefinition
 * @param {object} systemProducts
 * */
export function replaceProductInSystemDefinition(slotName, systemDefinition, systemProducts, productData) { // eslint-disable-line
  const { slots } = systemDefinition.attributes;
  // old data
  const data = slots[slotName];
  if (!data) {
    return false;
  }

  const oldOptions = data.options;

  const controlProductInfo = getControlProductInfoBySlotName(slotName, systemDefinition, systemProducts);
  if (!controlProductInfo || !controlProductInfo.set || !controlProductInfo.product) {
    return false;
  }
  const numSlots = Object.keys(slots).length;

  if (productData) {
    let newProductData = null;
    if (productData instanceof ControlProductInfo) {
      newProductData = productData.product;
    } else {
      newProductData = productData;
    }

    const inst = instantiate(newProductData, slotName, systemDefinition, systemProducts, null);

    // Transfer old options to new product if available in the accessories list
    const numOldOptions = oldOptions ? oldOptions.length : 0;
    const newOptions = inst.options;
    for (let i = 0; i < numOldOptions; i += 1) {
      const option = oldOptions[i];
      // Find 'old' product accessory in new product data
      // (returns null if not found, so won't be added in the new options list)
      const productAccessoryInfo = findProductAccessory(option, newProductData);
      if (productAccessoryInfo) {
        const productAccessory = productAccessoryInfo.accessory;
        const productAccessoryType = productAccessoryInfo.type;

        if (productAccessoryType === 'optional') {
          defAddOptionalAccessoryToArray(
            productAccessory,
            productAccessoryInfo.set,
            newOptions,
            systemDefinition,
            systemProducts,
          );
        } else {
          defAddAccessoryToArray(productAccessory, newOptions, systemDefinition, systemProducts);
        }
      }
    }

    slots[slotName] = inst;
  } else {
    // Remove
    let numControlSlots = 0;
    const activeSlotData = [];
    for (let i = 0; i < numSlots; i += 1) {
      const otherSlotName = `controls-${i + 1}`;
      const otherSlotData = slots[otherSlotName];
      if (otherSlotData || otherSlotData === false) {
        numControlSlots += 1;
      }
      if (otherSlotData && (otherSlotName !== slotName)) {
        activeSlotData.push({ slotData: otherSlotData, slotName: otherSlotName });
      }
    }

    activeSlotData.sort(controlSortCompare);
    for (let i = 0; i < numControlSlots; i += 1) {
      const otherSlotName = `controls-${i + 1}`;
      const slotObject = activeSlotData[i];
      const resData = slotObject ? slotObject.slotData : false;
      slots[otherSlotName] = resData;
    }
  }

  // Move options from old data to next data with same ID
  if (data && data.options && data.options.length > 0) {
    for (let i = 0; i < numSlots; i += 1) {
      const otherSlotName = `controls-${i + 1}`;
      if (otherSlotName !== slotName) {
        const slotData = slots[otherSlotName];
        if (slotData && slotData.id === data.id) {
          if (slotData.options && !slotData.options.length) {
            slotData.options = data.options;
            break;
          }
        }
      }
    }
  }
  reorderAccessoriesIfNeeded(systemDefinition, systemProducts);

  return true;
}

export function removeProductFromSystemDefinition(slotName, systemDefinition, systemProducts) { // eslint-disable-line
  return replaceProductInSystemDefinition(slotName, systemDefinition, systemProducts, null);
}

export function addProductToSystemDefinition(type, productData, systemDefinition, systemProducts) {
  const { slots } = systemDefinition.attributes;
  const numSlots = Object.keys(slots).length;
  let productInfo = productData;
  if (productData instanceof ControlProductInfo) {
    productInfo = productData.product;
  }

  let countSlots = 0;
  const controlSlots = [];
  for (let i = 0; i < numSlots; i += 1) {
    const controlSlotName = `controls-${i + 1}`;
    const controlData = slots[controlSlotName];
    if (typeof (controlData) !== 'undefined') {
      countSlots += 1;
      controlSlots.push({
        slotName: controlSlotName,
        slotData: controlData,
      });
    }
  }
  const amount = productInfo.amount || 1;
  for (let i = 0; i < amount; i += 1) {
    controlSlots.push({
      slotData: instantiate(productInfo, type, systemDefinition, systemProducts),
    });
  }
  controlSlots.sort(controlSortCompare);

  const numControlSlots = controlSlots.length;
  countSlots = countSlots > numControlSlots ? countSlots : numControlSlots;
  for (let i = 0; i < countSlots; i += 1) {
    const slotName = `controls-${i + 1}`;
    const slotObject = controlSlots[i];
    const slotData = slotObject ? slotObject.slotData : null;
    if (slotData) {
      slots[slotName] = slotData;
    } else {
      const oldValue = slots[slotName];
      const oldValueType = typeof (oldValue);
      if (oldValueType === 'boolean' || oldValueType === 'object') {
        slots[slotName] = false;
      }
    }
  }
  reorderAccessoriesIfNeeded(systemDefinition, systemProducts);
}

// Accessories

function accessoryAlreadyInPreviousControl(accessoryId, refSlotName, systemDefinition, systemProducts, onlyOptionalAccessories) {
  if (!systemDefinition) {
    return false;
  }
  const { attributes: { slots } } = systemDefinition;
  const numSlots = Object.keys(slots).length;
  for (let i = 0; i < numSlots; i += 1) {
    const otherSlotName = `controls-${i + 1}`;
    if (otherSlotName === refSlotName) {
      break;
    } else {
      const value = slots[otherSlotName];
      if (value) {
        const product = getProductInfoBySlotName(otherSlotName, systemDefinition, systemProducts);
        const accessories = defGetAccessoriesArrayByProduct(product, onlyOptionalAccessories);
        const numAcc = accessories ? accessories.length : 0;
        for (let j = 0; j < numAcc; j += 1) {
          const acc = accessories[j];
          if (acc && acc.id === accessoryId) {
            return true;
          }
        }
      }
    }
  }
  return false;
}

export function getSystemObjectAccessoriesByData(
  slotName,
  inputData,
  systemDefinition,
  systemProducts,
  productParam,
  onlyOptionalAccessories,
) {
  let data = inputData;
  if (!data && slotName) {
    data = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
  }
  if (!data || !systemDefinition || !systemProducts) {
    // #if DEBUG
    console.warn('getSystemObjectAccessories - Invalid parameters:', { slotName, systemDefinition, systemProducts });
    // #endif
    return null;
  }

  let product = productParam;

  if (!product) {
    if (!data) {
      return null;
    }
    const currentId = data.id;
    if (currentId === null || typeof (currentId) === 'undefined') {
      return null;
    }

    const productResult = getProductsBySlotNameAndId(slotName, currentId, systemProducts);
    product = Array.isArray(productResult) ? productResult[0] : productResult;
  }
  const productId = product ? product.id : false;

  const { slots } = systemDefinition.attributes;

  let firstSlot = null;
  let firstWirelessSlot = null;

  const numSlots = Object.keys(slots).length;

  for (let i = 0; i < numSlots; i += 1) {
    const otherSlotName = `controls-${i + 1}`;
    const contr = slots[otherSlotName];
    if (contr) {
      const otherContrProduct = getProductInfoBySlotName(otherSlotName, systemDefinition, systemProducts);
      const otherContrProductId = otherContrProduct.id;
      if (otherContrProduct && otherContrProductId === productId) {
        if (otherContrProduct.wireless) {
          if (!firstWirelessSlot) {
            firstWirelessSlot = otherSlotName;
          }
        }
        if (!firstSlot) {
          firstSlot = otherSlotName;
        }
      }
    }
  }

  let result = defGetAccessoriesArrayByProduct(product, onlyOptionalAccessories);

  if (result) {
    const numResult = result.length;
    let filteredResult = result;
    for (let i = 0; i < numResult; i += 1) {
      const accessory = result[i];
      if (
        accessory.keep_in_all_slots ||
          !accessoryAlreadyInPreviousControl(accessory.id, slotName, systemDefinition, systemProducts, onlyOptionalAccessories)
      ) {
        // add
        if (filteredResult !== result) {
          filteredResult.push(result);
        }
      } else if (filteredResult === result) {
        filteredResult = result ? result.slice(0, i) : [];
      }
    }
    result = filteredResult;
  }

  return result;
}

// Keep in case this was correct
// TODO: remove later
export function oldGetSystemObjectAccessoriesByData(
  slotName,
  inputData,
  systemDefinition,
  systemProducts,
  productParam,
  onlyOptionalAccessories,
) {
  let data = inputData;
  if (!data && slotName) {
    data = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
  }
  if (!data || !systemDefinition || !systemProducts) {
    // #if DEBUG
    console.warn('getSystemObjectAccessories - Invalid parameters:', { slotName, systemDefinition, systemProducts });
    // #endif
    return null;
  }

  let product = productParam;

  if (!product) {
    if (!data) {
      return null;
    }
    const currentId = data.id;
    if (currentId === null || typeof (currentId) === 'undefined') {
      return null;
    }

    const productResult = getProductsBySlotNameAndId(slotName, currentId, systemProducts);
    product = Array.isArray(productResult) ? productResult[0] : productResult;
  }
  const isWireless = product ? product.wireless : false;
  const productId = product ? product.id : false;

  const { slots } = systemDefinition.attributes;

  let firstSlot = null;
  let firstWirelessSlot = null;
  // let firstWiredSlot = null;

  const numSlots = Object.keys(slots).length;

  for (let i = 0; i < numSlots; i += 1) {
    const otherSlotName = `controls-${i + 1}`;
    const contr = slots[otherSlotName];
    if (contr) {
      const otherContrProduct = getProductInfoBySlotName(otherSlotName, systemDefinition, systemProducts);
      const otherContrProductId = otherContrProduct.id;
      // const otherContrProduct = getProductsBySlotNameAndId(otherSlotName, contr.id, systemProducts);
      if (otherContrProduct && otherContrProductId === productId) {
        if (otherContrProduct.wireless) {
          if (!firstWirelessSlot) {
            firstWirelessSlot = otherSlotName;
          }
        } /* else if (!firstWiredSlot) {
          firstWiredSlot = otherSlotName;
        } */
        if (!firstSlot) {
          firstSlot = otherSlotName;
        }
      }
    }
  }
  const isFirstController = firstSlot === slotName;
  // const isFirstWired = firstWiredSlot === slotName;

  /*
  if (firstSlot !== slotName) {
    return null;
  }
  // */

  let result = defGetAccessoriesArrayByProduct(product, onlyOptionalAccessories);

  if (result) {
    const numResult = result.length;
    let filteredResult = result;
    for (let i = 0; i < numResult; i += 1) {
      const accessory = result[i];
        /*
        isWireless && accessory.only_wireless && isFirstWireless

        isFirstController && !accessory.only_wireless

        accessory.keep_in_all_slots
        */
      let includeAccessory = false;

      if (accessory.keep_in_all_slots) {
        includeAccessory = true;
      } else if (isWireless && accessory.only_wireless) {
        // Check if accessory is already available in previous wireless controllers
        // If so, don't include the accessory
        let loopSearchAccessory = true;
        let alreadyInPrevController = false;
        for (let j = 0; loopSearchAccessory && j < numSlots; j += 1) {
          const otherSlotName = `controls-${j + 1}`;
          if (otherSlotName === slotName) {
            loopSearchAccessory = false;
          } else {
            const otherProduct = getProductInfoBySlotName(otherSlotName, systemDefinition, systemProducts);

            if (
              productHasAccessoryId(otherProduct, accessory.id, false) ||
              productHasAccessoryId(otherProduct, accessory.id, true)
            ) {
              loopSearchAccessory = false;
              alreadyInPrevController = true;
            }
            /*
            const otherAccessories = defGetAccessoriesArrayByProduct(otherProduct, onlyOptionalAccessories);
            const numOtherAccessories = otherAccessories ? otherAccessories.length : 0;
            for (let k = 0; loopSearchAccessory && k < numOtherAccessories; k += 1) {
              const otherAccessory = otherAccessories[k];
              const otherAccessoryId = otherAccessory ? otherAccessory.id : null;
              if (otherAccessoryId === accessory.id) {
                alreadyInPrevController = true;
                loopSearchAccessory = false;
              }
            }
            */
          }
        }
        if (!alreadyInPrevController) {
          includeAccessory = true;
        }
      } else if (isFirstController && !accessory.only_wireless) {
        includeAccessory = true;
      }

      if (
        includeAccessory
      ) {
        // add
        if (filteredResult !== result) {
          filteredResult.push(result);
        }
      } else if (filteredResult === result) {
        filteredResult = result ? result.slice(0, i) : [];
      }
    }
    result = filteredResult;

    /*
    if (firstSlot !== slotName) {
      // Only show accessories with 'keep_in_all_slots': true
      if (result) {
        result = result.filter(filterKeepInAllSlots);
      }
    }
    */
  }

  return result;
}

export function getSystemObjectAccessories(slotName, systemDefinition, systemProducts, productParam, onlyOptionalAccessories) {
  if (!slotName || !systemDefinition || !systemProducts) {
    // #if DEBUG
    console.warn('getSystemObjectAccessories - Invalid parameters:', { slotName, systemDefinition, systemProducts });
    // #endif
    return null;
  }

  const data = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);

  return getSystemObjectAccessoriesByData(slotName, data, systemDefinition, systemProducts, productParam, onlyOptionalAccessories);
  /*
  let product = productParam;

  if (!product) {
    const objectDef = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
    if (!objectDef) {
      return null;
    }
    const currentId = objectDef.id;
    if (currentId === null || typeof (currentId) === 'undefined') {
      return null;
    }

    const productResult = getProductsBySlotNameAndId(slotName, currentId, systemProducts);
    product = Array.isArray(productResult) ? productResult[0] : productResult;
  }
  return defGetAccessoriesArrayByProduct(product, onlyOptionalAccessories);
  */
}



// WEBPACK FOOTER //
// ./js/utils/project-definition/component/controls/index.js